import { cardInCampaign } from './performanceCards';

// Presentation metadata only. Never change the original suggestion or its apply index.
export function recommendationPlatform(card, campaigns = []) {
  const destination = card.dest || campaigns.find((c) => (c.id || c.key) === card.camp)?.destination || '';
  if (card.origin === 'Google' || /google|g-ads|gads|pmax|demand.?gen|^search$/i.test(destination)) return 'Google Ads';
  if (/facebook|meta|instagram|^fb$/i.test(destination)) return 'Meta';
  if (/dv360/i.test(destination)) return 'DV360';
  if (/ttd|trade.?desk/i.test(destination)) return 'The Trade Desk';
  if (/linkedin/i.test(destination)) return 'LinkedIn';
  return destination || 'Unknown platform';
}

export function optimizationType(card) {
  const text = [card.tt, card.action, card.cat].filter(Boolean).join(' ').toLowerCase();
  if (/keyword|search.theme|negative/.test(text)) return 'Keywords & search themes';
  if (/ad.?set/.test(text)) return 'Ad sets';
  if (/asset.?group/.test(text)) return 'Asset groups';
  if (/ad.?group/.test(text)) return 'Ad groups';
  if (/budget|bid|pacing|roas|cpa/.test(text)) return 'Bids & budgets';
  if (/audience|target|geo|placement/.test(text)) return 'Audiences & targeting';
  if (/creative|asset|\bad\b|\bads\b/.test(text)) return 'Ads & creative';
  if (/experiment|test/.test(text)) return 'Experiments';
  return 'Campaign & other';
}

export const recommendationSource = (card) => card.origin === 'Google' ? 'Google recommendations' : 'Sidekick additions';

export function matchesRecommendation(card, filters, campaigns) {
  return (!filters.campaigns.length || filters.campaigns.some((key) => cardInCampaign(card, key)))
    && (filters.platform === 'all' || recommendationPlatform(card, campaigns) === filters.platform)
    && (filters.type === 'all' || optimizationType(card) === filters.type)
    && (filters.source === 'all' || recommendationSource(card) === filters.source);
}

// Compare estimates only within a common metric, unit, direction and horizon.
// Priority is the cross-metric fallback; $ / month must never outrank CPA %
// merely because its raw number is larger.
export function estimatedImpact(card) {
  const raw = card.raw || {};
  const gain = raw.expected_gain || raw.scoring?.expected_lift || raw.expected_lift || {};
  if (gain.status === 'unknown' && gain.scope === 'demandgen') return { value: null, group: null, label: 'Impact unknown — test required' };
  const band = String(raw.expected_impact || '').toLowerCase();
  const absolute = gain.absolute_per_month;
  const point = gain.point_pct ?? card.lp;
  const value = absolute ?? point;
  const metric = gain.metric || card.lm;
  if (value !== null && value !== undefined && value !== '' && typeof value !== 'boolean' && Number.isFinite(Number(value)) && metric) {
    const direction = gain.direction || card.ld || 'direction unspecified';
    const monetary = /spend|cost|value|revenue|cpa|cpc|cpm/i.test(metric);
    const currency = gain.currency_code || gain.currency || card.ccy;
    const horizon = absolute != null ? 'per month' : gain.horizon_days != null
      ? `${gain.horizon_days} days` : gain.time_horizon || 'horizon unspecified';
    // Unknown currency amounts cannot be ranked across campaigns. Counts and
    // percentage changes do not need currency conversion.
    const unit = absolute == null ? '%' : monetary ? currency || `unknown currency:${card.camp || card.id}` : 'count';
    const group = `${String(metric).toLowerCase()} ${direction} ${unit} ${horizon}`;
    const labelUnit = absolute == null ? '%' : monetary ? ` ${currency || '(currency unknown)'}` : '';
    return { group, value: Math.abs(Number(value)), label: `${value}${labelUnit} ${metric} · ${direction} · ${horizon} (estimated)` };
  }
  if (['high', 'medium', 'low'].includes(band)) return { group: 'Impact band', value: { high: 3, medium: 2, low: 1 }[band], label: `${band} estimated impact` };
  return { group: null, value: null, label: 'Impact not estimated' };
}

export function sortByEstimatedImpact(cards) {
  // Housekeeping cards (sortByPriority's `maintenance`) rank after every
  // substantive one here too, whatever their estimate; each half keeps the
  // ordering below.
  const substantive = cards.filter((card) => !card.maintenance);
  if (substantive.length > 0 && substantive.length !== cards.length) {
    return [...sortByEstimatedImpact(substantive),
      ...sortByEstimatedImpact(cards.filter((card) => card.maintenance))];
  }
  // Group comparable estimates first, then order groups by highest agent priority.
  // A group-based ordering is transitive even with unlike estimates in the feed.
  const groups = new Map();
  for (const card of cards) {
    const impact = estimatedImpact(card);
    const key = impact.group || 'Unestimated';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(card);
  }
  return [...groups.entries()].sort(([ak, a], [bk, b]) => {
    if (ak === 'Unestimated' || bk === 'Unestimated') return ak === bk ? 0 : ak === 'Unestimated' ? 1 : -1;
    return Math.max(...b.map((c) => c.pri || 0)) - Math.max(...a.map((c) => c.pri || 0)) || ak.localeCompare(bk);
  }).flatMap(([, group]) => group.sort((a, b) => (estimatedImpact(b).value || 0) - (estimatedImpact(a).value || 0)
    || (b.pri || 0) - (a.pri || 0) || a.index - b.index));
}
